import { useEffect, useState } from 'react';
import GoBack from 'components/actions/GoBack';
import { Link } from 'react-router-dom';
import { MdSmartToy, MdContentCopy, MdCheck, MdOutlineVpnKey, MdOutlineDescription, MdLink, MdWarningAmber } from 'react-icons/md';
import SellerApiKeyPanel from 'views/admin/main/profile/settings/components/SellerApiKeyPanel';
import { CONFIG } from 'variables/config';
import { useIndustries } from 'services/industryService';
import { revealApiKey, maskApiKey } from 'services/apiKeyReveal';
import { AccountPrice, accountPriceFor, fetchAccountPrices, PRICES_PATH } from 'services/integrations';

// Seller docs page for the site-generation MCP server (leadrs-back's POST /mcp, see
// services/mcp/): the seller creates their account-level API key
// (same key as the generic API, seller/apiDocs), plugs the MCP URL + key into their AI tool, and
// asks it to generate a site whose lead forms already feed this account. The agent-facing guide
// itself is served by the backend (GET /mcp/guide.md) — single source shared with the MCP resource.

// Same display rule as the API docs pages: show the public hostname, not the Scalingo one.
const API_URL = CONFIG.apiUrl.replace(/\/$/, '');
const DISPLAY_API_URL = API_URL === 'https://api-leadrs-co.osc-fr1.scalingo.io' ? 'https://api.leadrs.co' : API_URL;
const MCP_URL = `${DISPLAY_API_URL}/mcp`;
const GUIDE_URL = `${DISPLAY_API_URL}/mcp/guide.md`;

const KEY_PLACEHOLDER = '<VOTRE_CLE_API>';

type ClientId = 'claude-code' | 'cursor' | 'vscode' | 'claude-desktop' | 'windsurf';

interface ClientConfig {
    id: ClientId;
    label: string;
    where: string;
    language: 'bash' | 'json';
    build: (key: string) => string;
    note?: string;
}

const CLIENTS: ClientConfig[] = [
    {
        id: 'claude-code',
        label: 'Claude Code',
        where: 'Dans un terminal, à la racine du projet du site :',
        language: 'bash',
        build: (key) => `claude mcp add --transport http leadrs ${MCP_URL} --header "x-api-key: ${key}"`,
        note: 'Vérifiez ensuite avec /mcp dans Claude Code que le serveur « leadrs » est connecté.',
    },
    {
        id: 'cursor',
        label: 'Cursor',
        where: 'Dans ~/.cursor/mcp.json (configuration globale, hors du dépôt) :',
        language: 'json',
        build: (key) =>
            JSON.stringify({ mcpServers: { leadrs: { url: MCP_URL, headers: { 'x-api-key': key } } } }, null, 2),
    },
    {
        id: 'vscode',
        label: 'VS Code (Copilot)',
        where: 'Dans .vscode/mcp.json — la clé est demandée au démarrage et n’est jamais écrite dans le fichier :',
        language: 'json',
        build: () =>
            JSON.stringify(
                {
                    inputs: [{ type: 'promptString', id: 'leadrs-api-key', description: 'Clé API Leadrs', password: true }],
                    // Literal VS Code input variable, resolved by VS Code itself.
                    // eslint-disable-next-line no-template-curly-in-string
                    servers: { leadrs: { type: 'http', url: MCP_URL, headers: { 'x-api-key': '${input:leadrs-api-key}' } } },
                },
                null,
                2
            ),
    },
    {
        id: 'claude-desktop',
        label: 'Claude Desktop',
        where: 'Dans claude_desktop_config.json (Réglages → Développeur → Modifier la configuration), via le pont mcp-remote (Node.js requis) :',
        language: 'json',
        build: (key) =>
            JSON.stringify(
                {
                    mcpServers: {
                        leadrs: {
                            command: 'npx',
                            // Literal env reference expanded by mcp-remote (avoids a space inside an arg).
                            // eslint-disable-next-line no-template-curly-in-string
                            args: ['-y', 'mcp-remote', MCP_URL, '--header', 'Authorization:${LEADRS_AUTH}'],
                            env: { LEADRS_AUTH: `Bearer ${key}` },
                        },
                    },
                },
                null,
                2
            ),
        note: 'Redémarrez Claude Desktop après avoir enregistré le fichier.',
    },
    {
        id: 'windsurf',
        label: 'Windsurf',
        where: 'Dans ~/.codeium/windsurf/mcp_config.json :',
        language: 'json',
        build: (key) =>
            JSON.stringify({ mcpServers: { leadrs: { serverUrl: MCP_URL, headers: { 'x-api-key': key } } } }, null, 2),
    },
];

const TOOLS = [
    { name: 'list_products', description: 'Catalogue des activités Leadrs (subIndustryId), indique celles qui ont des acheteurs en ce moment et vos prix déjà fixés.' },
    { name: 'setup_site', description: 'Crée l’intégration du site pour un produit, fixe votre prix minimum et le style du formulaire, et renvoie les snippets Pixel + iframe. Fonctionne sur tout domaine, le domaine final peut être ajouté plus tard.' },
    { name: 'set_minimum_price', description: 'Fixe votre prix minimum par lead pour un produit (le même que dans Intégrations → Mes prix).' },
    { name: 'get_site_status', description: 'Vérifie que le formulaire s’affiche, que le Pixel est détecté, qu’un prix et des acheteurs existent, et montre les derniers leads reçus.' },
    { name: 'update_form_style', description: 'Accorde les couleurs, la police et le texte du bouton du formulaire au design du site.' },
    { name: 'register_pages', description: 'Facultatif : déclare les pages à l’avance (elles s’ajoutent sinon toutes seules au premier affichage du formulaire).' },
    { name: 'get_account', description: 'Votre compte vendeur, vos intégrations existantes et vos prix.' },
];

interface PromptProduct {
    industry: string;
    subIndustry: string;
    subIndustryId: number;
}

type PromptMode = 'generate' | 'integrate';

const PROMPT_MODES: Array<{ id: PromptMode; title: string; description: string }> = [
    { id: 'generate', title: 'Générer le site et l’intégrer à Leadrs', description: 'L’agent crée un nouveau site complet, déjà relié à votre compte.' },
    { id: 'integrate', title: 'Intégrer Leadrs sur mon site existant', description: 'L’agent ajoute le Pixel et le formulaire Leadrs au code de votre site actuel.' },
];

// English on purpose: it's addressed to the coding agent, not to the seller. A generated site's
// own content stays in French (the guide's default for Leadrs sellers' audiences). Everything the
// seller already gave here (product, price, domain) is spelled out so the agent has nothing left to
// ask; what's missing is left to the guide's "ask at most once" rule.
const buildPrompt = (mode: PromptMode, product: PromptProduct | null, siteUrl: string, minimumPrice: string) => {
    const price = Number(minimumPrice.replace(',', '.'));
    const lines = [
        mode === 'generate'
            ? 'Build a lead-generation website for my business and wire it to my Leadrs seller account through the "leadrs" MCP server, as fast as possible.'
            : 'Wire my existing website (this codebase) to my Leadrs seller account through the "leadrs" MCP server, as fast as possible, so the leads it collects go straight to my account.',
        product
            ? `Leadrs product: ${product.industry} / ${product.subIndustry} (subIndustryId ${product.subIndustryId}).`
            : mode === 'generate'
            ? 'My activity: <DESCRIBE YOUR ACTIVITY>. Pick the matching Leadrs product with list_products.'
            : "Infer my activity from the site's content and pick the matching Leadrs product with list_products.",
        Number.isFinite(price) && price > 0 ? `My minimum price per lead for this product: ${price} € (pass it as minimumPrice to setup_site).` : null,
        siteUrl.trim()
            ? `${mode === 'generate' ? 'Final domain' : 'Public URL of my site'}: ${siteUrl.trim()} (pass it as siteUrl to setup_site).`
            : mode === 'generate'
            ? "The final domain isn't known yet: call setup_site without siteUrl, the form works on any domain."
            : null,
        `Read the resource leadrs://guides/site-generation (also at ${GUIDE_URL}) first and follow its fast workflow: ask me at most one question, only for what is missing above.`,
        mode === 'generate'
            ? "Write the site's content in French. Don't invent reviews, certifications or figures: leave clearly marked placeholders and list them for me."
            : "Only add the Pixel and the Leadrs form, styled like the site; don't change anything else. If the site already has its own contact or quote form, ask me before replacing it.",
        'When the site runs (a local dev server is fine), load a page containing the form and check everything with get_site_status before reporting back to me.',
    ];
    return lines.filter(Boolean).join('\n');
};

// Starts an interactive Claude Code session with the prompt already sent (heredoc keeps quotes intact).
const buildClaudeCodeCommand = (prompt: string) => `claude "$(cat <<'EOF'\n${prompt}\nEOF\n)"`;

const useCopy = () => {
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const copy = (id: string, text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
        });
    };
    return { copiedId, copy };
};

const CopyButton = ({ id, text, label, copiedId, copy, variant = 'brand' }: { id: string; text: string; label: string; copiedId: string | null; copy: (id: string, text: string) => void; variant?: 'brand' | 'light' | 'outline' }) => {
    const styles = {
        brand: 'bg-leadrs-brand text-white hover:opacity-80',
        light: 'bg-white text-leadrs-text hover:opacity-90',
        outline: 'border border-white/30 text-white hover:bg-white/10',
    }[variant];
    return (
        <button onClick={() => copy(id, text)} className={`flex items-center gap-1.5 rounded-lg px-3 py-2 font-geist text-xs font-medium transition-opacity ${styles}`}>
            {copiedId === id ? <MdCheck className="h-4 w-4" /> : <MdContentCopy className="h-4 w-4" />}
            {copiedId === id ? 'Copié' : label}
        </button>
    );
};

// `copyText` lets the block show a masked API key while the clipboard gets the real one.
const CodeBlock = ({ id, code, copyText, copiedId, copy }: { id: string; code: string; copyText?: string; copiedId: string | null; copy: (id: string, text: string) => void }) => (
    <div className="relative mt-3">
        <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3 pr-24 font-mono text-xs text-leadrs-text">
            {code}
        </pre>
        <button
            onClick={() => copy(id, copyText ?? code)}
            className="absolute right-2 top-2 flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-2.5 py-1.5 font-geist text-xs text-white transition-opacity hover:opacity-80"
        >
            {copiedId === id ? <MdCheck className="h-3.5 w-3.5" /> : <MdContentCopy className="h-3.5 w-3.5" />}
            {copiedId === id ? 'Copié' : 'Copier'}
        </button>
    </div>
);

const StepTitle = ({ step, title }: { step: number; title: string }) => (
    <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-leadrs-brand font-geist text-sm font-semibold text-white">
            {step}
        </span>
        <h3 className="font-geist text-base font-semibold text-leadrs-text">{title}</h3>
    </div>
);

const SellerMcpDocs = () => {
    const [apiKey, setApiKey] = useState<string | null>(null);
    const [keyBusy, setKeyBusy] = useState(false);
    const [keyError, setKeyError] = useState('');
    const [clientId, setClientId] = useState<ClientId>('claude-code');
    const [industryName, setIndustryName] = useState('');
    const [subIndustryName, setSubIndustryName] = useState('');
    const { industries } = useIndustries();
    const [siteUrl, setSiteUrl] = useState('');
    const [minimumPrice, setMinimumPrice] = useState('');
    // Prefilled with the account price of the chosen product (Mes prix), which the agent's
    // setup_site / set_minimum_price write back to: one price per activity for the whole account.
    const [priceTouched, setPriceTouched] = useState(false);
    const [accountPrices, setAccountPrices] = useState<AccountPrice[]>([]);
    useEffect(() => {
        fetchAccountPrices()
            .then(setAccountPrices)
            .catch(() => {});
    }, []);
    const [promptMode, setPromptMode] = useState<PromptMode>('generate');
    const { copiedId, copy } = useCopy();

    // Fetched from the backend only on an explicit click, kept in memory only (never in storage).
    const handleInsertKey = async () => {
        setKeyBusy(true);
        setKeyError('');
        try {
            setApiKey(await revealApiKey());
        } catch (e: any) {
            setKeyError(e?.response?.data?.message || 'Impossible de récupérer votre clé API. Créez-la ci-dessus si besoin.');
        } finally {
            setKeyBusy(false);
        }
    };

    const client = CLIENTS.find((c) => c.id === clientId) || CLIENTS[0];
    const clientConfig = client.build(apiKey ? maskApiKey(apiKey) : KEY_PLACEHOLDER);
    const clientConfigToCopy = client.build(apiKey || KEY_PLACEHOLDER);
    const selectedIndustry = industries.find((i) => i.name === industryName) || null;
    const subIndustryOptions = selectedIndustry?.subIndustries || [];
    const selectedSubIndustry = subIndustryOptions.find((s) => s.name === subIndustryName) || null;
    const productAccountPrice = industryName ? accountPriceFor(accountPrices, industryName, subIndustryName) : undefined;
    useEffect(() => {
        if (!priceTouched) setMinimumPrice(productAccountPrice != null ? String(productAccountPrice) : '');
    }, [productAccountPrice, priceTouched]);
    const promptProduct: PromptProduct | null =
        selectedIndustry && selectedSubIndustry && selectedSubIndustry.subIndustryId != null
            ? { industry: selectedIndustry.name, subIndustry: selectedSubIndustry.name, subIndustryId: selectedSubIndustry.subIndustryId }
            : null;
    const prompt = buildPrompt(promptMode, promptProduct, siteUrl, minimumPrice);

    return (
        <>
            <div className="mb-3 flex items-center justify-between gap-3">
                <GoBack />
                <Link to="/integrations" className="font-geist text-sm font-semibold text-leadrs-brand hover:underline">
                    Mes intégrations →
                </Link>
            </div>
            <div className="w-full">
                <div className="rounded-xl border border-leadrs-brand bg-leadrs-text px-6 py-5">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
                            <MdSmartToy className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <h2 className="font-crimson text-2xl font-semibold leading-tight text-white">
                                Générez votre site avec l'IA, déjà relié à votre compte Leadrs
                            </h2>
                            <p className="mt-1 font-geist text-sm text-white/70">
                                Branchez le serveur MCP Leadrs sur votre agent IA (Claude, Cursor, VS Code...) avec votre
                                clé API : il génère le site, y intègre votre formulaire et votre Pixel, et chaque lead
                                reçu arrive directement dans votre compte. Aucune configuration technique de votre côté.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <CopyButton id="hero-url" text={MCP_URL} label="Copier l'URL du MCP" copiedId={copiedId} copy={copy} variant="light" />
                                <CopyButton id="hero-guide" text={GUIDE_URL} label="Copier le lien du guide .md" copiedId={copiedId} copy={copy} variant="outline" />
                                <a
                                    href={`${API_URL}/mcp/guide.md`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-2 font-geist text-xs font-medium text-white hover:bg-white/10"
                                >
                                    Ouvrir le .md
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {[
                        ['Clé API', 'Créez votre clé vendeur, valable pour l’API et le MCP.'],
                        ['Connexion', 'Ajoutez l’URL du MCP et votre clé dans votre outil IA.'],
                        ['Génération', 'Choisissez votre produit et votre prix : l’agent crée le site, le relie à votre compte et vérifie que tout fonctionne.'],
                    ].map(([title, text], i) => (
                        <div key={title} className="rounded-xl border border-leadrs-stroke bg-white px-4 py-4">
                            <p className="font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-brand">Étape {i + 1}</p>
                            <p className="mt-1 font-geist text-sm font-semibold text-leadrs-text">{title}</p>
                            <p className="mt-1 font-geist text-xs text-leadrs-text-soft">{text}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-stroke bg-white px-6 py-5">
                    <StepTitle step={1} title="Créez votre clé API" />
                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                        C'est la même clé que pour l'API d'envoi de leads (une seule clé par compte). Elle authentifie
                        votre agent auprès du MCP : tout ce qu'il crée est rattaché à votre compte vendeur. Elle n'est
                        jamais affichée sans que vous le demandiez.
                    </p>
                    <div className="mt-4">
                        <SellerApiKeyPanel />
                    </div>
                    <div className="mt-4 rounded-lg border border-leadrs-yellow-focus bg-leadrs-yellow-soft px-4 py-4">
                        <div className="flex items-center gap-2">
                            <MdOutlineVpnKey className="h-5 w-5 text-leadrs-yellow-focus" />
                            <p className="font-geist text-sm font-semibold text-leadrs-text">Insérer votre clé dans les exemples</p>
                        </div>
                        <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                            Facultatif — par défaut, les configurations ci-dessous contiennent{' '}
                            <code className="font-mono">{KEY_PLACEHOLDER}</code>. Sur demande, votre clé est récupérée
                            depuis votre compte : elle reste masquée à l'écran mais est incluse quand vous copiez une
                            configuration. Elle n'est jamais enregistrée dans votre navigateur.
                        </p>
                        {keyError && <p className="mt-2 font-geist text-xs text-leadrs-red-focus">{keyError}</p>}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            {apiKey ? (
                                <>
                                    <code className="rounded-md bg-white px-3 py-2 font-mono text-xs text-leadrs-text">{maskApiKey(apiKey)}</code>
                                    <button
                                        onClick={() => setApiKey(null)}
                                        className="rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-xs font-medium text-leadrs-text-soft hover:bg-white"
                                    >
                                        Retirer ma clé des exemples
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleInsertKey}
                                    disabled={keyBusy}
                                    className="rounded-lg bg-leadrs-brand px-3 py-2 font-geist text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                                >
                                    {keyBusy ? 'Récupération...' : 'Insérer ma clé dans les exemples'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-stroke bg-white px-6 py-5">
                    <StepTitle step={2} title="Connectez le MCP à votre outil IA" />
                    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                        <MdLink className="h-5 w-5 text-leadrs-brand" />
                        <span className="font-geist text-sm text-leadrs-text-soft">URL du serveur MCP :</span>
                        <code className="flex-1 break-all font-mono text-sm font-semibold text-leadrs-text">{MCP_URL}</code>
                        <CopyButton id="url" text={MCP_URL} label="Copier" copiedId={copiedId} copy={copy} />
                    </div>
                    <p className="mt-3 font-geist text-sm text-leadrs-text-soft">
                        Transport <strong>HTTP (Streamable HTTP)</strong>. Authentification : en-tête{' '}
                        <code className="font-mono text-leadrs-brand">x-api-key: &lt;votre clé&gt;</code> ou{' '}
                        <code className="font-mono text-leadrs-brand">Authorization: Bearer &lt;votre clé&gt;</code>.
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                        {CLIENTS.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setClientId(c.id)}
                                className={`rounded-lg px-3 py-1.5 font-geist text-xs font-medium transition-colors ${
                                    c.id === clientId ? 'bg-leadrs-brand text-white' : 'border border-leadrs-stroke text-leadrs-text hover:bg-leadrs-bg'
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <p className="mt-3 font-geist text-sm text-leadrs-text-soft">{client.where}</p>
                    <CodeBlock id={`client-${client.id}`} code={clientConfig} copyText={clientConfigToCopy} copiedId={copiedId} copy={copy} />
                    {client.note && <p className="mt-2 font-geist text-xs text-leadrs-text-soft">{client.note}</p>}
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-stroke bg-white px-6 py-5">
                    <StepTitle step={3} title="Demandez à votre agent de faire le travail" />
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {PROMPT_MODES.map((m, i) => (
                            <button
                                key={m.id}
                                onClick={() => setPromptMode(m.id)}
                                className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                                    m.id === promptMode ? 'border-leadrs-brand bg-leadrs-purple-soft' : 'border-leadrs-stroke hover:bg-leadrs-bg'
                                }`}
                            >
                                <p className="font-geist text-sm font-semibold text-leadrs-text">
                                    {i + 1}. {m.title}
                                </p>
                                <p className="mt-0.5 font-geist text-xs text-leadrs-text-soft">{m.description}</p>
                            </button>
                        ))}
                    </div>
                    <p className="mt-3 font-geist text-sm text-leadrs-text-soft">
                        {promptMode === 'generate'
                            ? "Collez ce message dans votre agent. Plus vous lui donnez d'informations sur votre entreprise (nom, zone d'intervention, coordonnées, points forts), moins il laissera d'emplacements à compléter."
                            : "Ouvrez le projet de votre site dans votre agent et collez-lui ce message. Il n'ajoute que le Pixel et le formulaire Leadrs, et vous demande avant de toucher à un formulaire existant."}
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <select
                            value={industryName}
                            onChange={(e) => {
                                setIndustryName(e.target.value);
                                setSubIndustryName('');
                            }}
                            className="rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-sm text-leadrs-text"
                        >
                            <option value="">Choisir une industrie</option>
                            {industries.map((i) => (
                                <option key={i._id} value={i.name}>
                                    {i.name}
                                </option>
                            ))}
                        </select>
                        <select
                            value={subIndustryName}
                            onChange={(e) => setSubIndustryName(e.target.value)}
                            disabled={!selectedIndustry}
                            className="rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-sm text-leadrs-text disabled:opacity-50"
                        >
                            <option value="">Choisir un produit</option>
                            {subIndustryOptions.map((sub) => (
                                <option key={sub.name} value={sub.name} disabled={sub.subIndustryId == null}>
                                    {sub.name}
                                    {sub.subIndustryId == null ? ' (id manquant)' : ''}
                                </option>
                            ))}
                        </select>
                        <input
                            type="number"
                            min={1}
                            step="0.5"
                            value={minimumPrice}
                            onChange={(e) => {
                                setMinimumPrice(e.target.value);
                                setPriceTouched(true);
                            }}
                            placeholder="Votre prix minimum par lead (€)"
                            className="rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-sm text-leadrs-text"
                        />
                        <p className="font-geist text-xs text-leadrs-text-soft sm:col-span-2">
                            {productAccountPrice != null ? `Prix du compte pour ce produit : ${productAccountPrice} €, proposé par défaut. ` : ''}
                            Le prix donné à l'agent est enregistré comme prix du compte pour ce produit.{' '}
                            <Link to={PRICES_PATH} className="font-semibold text-leadrs-brand hover:underline">
                                Voir Mes prix
                            </Link>
                        </p>
                        <input
                            type="text"
                            value={siteUrl}
                            onChange={(e) => setSiteUrl(e.target.value)}
                            placeholder={promptMode === 'generate' ? 'Domaine final, si vous l’avez déjà (facultatif)' : 'URL de votre site (facultatif)'}
                            className="rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-sm text-leadrs-text"
                        />
                    </div>
                    <p className="mt-2 font-geist text-xs text-leadrs-text-soft">
                        Tout est facultatif, mais chaque champ rempli est une question de moins pour l'agent. Le prix
                        est celui de votre grille de prix : sans prix, vos leads ne vont qu'aux offres marketplace, pas
                        aux commandes permanentes des acheteurs. Le domaine peut être ajouté plus tard : le formulaire
                        fonctionne déjà en local et en préproduction.
                    </p>
                    <CodeBlock id="prompt" code={prompt} copiedId={copiedId} copy={copy} />
                    <p className="mt-4 font-geist text-sm text-leadrs-text-soft">
                        <strong className="text-leadrs-text">Le plus rapide avec Claude Code</strong> : dans le dossier du
                        projet (un dossier vide pour un nouveau site), lancez directement :
                    </p>
                    <CodeBlock id="claude-command" code={buildClaudeCodeCommand(prompt)} copiedId={copiedId} copy={copy} />
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-stroke bg-white px-6 py-5">
                    <h3 className="font-geist text-base font-semibold text-leadrs-text">Outils exposés par le MCP</h3>
                    <div className="mt-3 overflow-x-auto">
                        <table className="w-full min-w-[480px] text-left">
                            <tbody>
                                {TOOLS.map((tool) => (
                                    <tr key={tool.name} className="border-t border-leadrs-stroke first:border-t-0">
                                        <td className="py-2 pr-4 align-top">
                                            <code className="font-mono text-sm font-semibold text-leadrs-brand">{tool.name}</code>
                                        </td>
                                        <td className="py-2 font-geist text-sm text-leadrs-text-soft">{tool.description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-3 font-geist text-sm text-leadrs-text-soft">
                        Ressource : <code className="font-mono text-leadrs-brand">leadrs://guides/site-generation</code> — le
                        guide complet (workflow, règles d'intégration et de contenu), aussi disponible en Markdown :
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <MdOutlineDescription className="h-5 w-5 text-leadrs-brand" />
                        <code className="break-all font-mono text-sm text-leadrs-text">{GUIDE_URL}</code>
                        <CopyButton id="guide" text={GUIDE_URL} label="Copier" copiedId={copiedId} copy={copy} />
                    </div>
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-red-focus bg-leadrs-red-soft px-6 py-5">
                    <div className="flex items-center gap-2">
                        <MdWarningAmber className="h-5 w-5 text-leadrs-red-focus" />
                        <h3 className="font-geist text-base font-semibold text-leadrs-text">Sécurité</h3>
                    </div>
                    <ul className="mt-2 list-disc space-y-1 pl-5 font-geist text-sm text-leadrs-text-soft">
                        <li>
                            Votre clé API ne doit jamais apparaître dans le code du site ni dans un dépôt Git : elle ne sert
                            qu'à connecter votre outil IA. Le site n'utilise que le Pixel et l'iframe, qui ne sont pas secrets.
                        </li>
                        <li>
                            L'agent n'a pas besoin de créer de formulaire : l'iframe Leadrs gère les champs, le consentement
                            RGPD et les vérifications anti-fraude.
                        </li>
                        <li>
                            Clé exposée par erreur ? Régénérez-la à l'étape 1 : l'ancienne cesse immédiatement de fonctionner.
                        </li>
                    </ul>
                </div>
            </div>
        </>
    );
};

export default SellerMcpDocs;
